/**
 * Search Service
 * Handles search API calls for store brands and stores
 */

import apiClient from '../utils/api/ApiClient';
import { API_ENDPOINTS } from '../constants/api.constants';
import type {
    SearchStoreBrandsRequest,
    SearchStoreBrandsResponse,
    SearchStoresRequest,
    SearchStoresResponse,
    StoreBrandSearchResult,
    StoreSearchResult,
} from '../types/api.types';

/**
 * Search for store brands by name
 * @param searchText - Search text (minimum 3 characters required)
 * @returns Promise with array of store brand search results
 */
export const searchStoreBrands = async (searchText: string): Promise<StoreBrandSearchResult[]> => {
    // Validate minimum search length
    if (searchText.length < 3) {
        return [];
    }

    const requestBody: SearchStoreBrandsRequest = {
        searchText,
    };

    const response = await apiClient.post<SearchStoreBrandsResponse>(
        API_ENDPOINTS.MICROSITE.SEARCH_STORE_BRANDS,
        requestBody
    );

    return response.data || [];
};

/**
 * Search for stores within a specific store brand by name
 * @param storeBrandID - MongoDB ObjectId of the store brand
 * @param searchText - Search text (minimum 3 characters required)
 * @returns Promise with array of store search results
 */
export const searchStores = async (
    storeBrandID: string,
    searchText: string
): Promise<StoreSearchResult[]> => {
    // Validate inputs
    if (!storeBrandID || searchText.length < 3) {
        return [];
    }

    const requestBody: SearchStoresRequest = {
        storeBrandID,
        searchText,
    };

    const response = await apiClient.post<SearchStoresResponse>(
        API_ENDPOINTS.MICROSITE.SEARCH_STORES,
        requestBody,
        { headers: { 'X-Store-Brand-Id': storeBrandID } }
    );

    return response.data || [];
};
